/**
 * 将 UserCouponEntity（voucherapi.md §4.2）转为列表卡片展示结构。
 */

import { pickCouponI18nText } from "./voucherRules";

/**
 * 解析券 `applicableScenes`（JSON 数组字符串，见 voucherapi.md §1.4.4）。
 * @param {object} raw
 * @returns {string[]} 大写场景码列表
 */
export function parseUserCouponApplicableScenes(raw) {
  if (!raw || typeof raw !== "object") return [];
  const str = raw.applicableScenes ?? raw.applicable_scenes;
  if (str == null || str === "") return [];
  if (Array.isArray(str)) return str.map((x) => String(x).trim().toUpperCase()).filter(Boolean);
  try {
    const parsed = JSON.parse(String(str));
    if (!Array.isArray(parsed)) return [];
    return parsed.map((x) => String(x).trim().toUpperCase()).filter(Boolean);
  } catch {
    return [];
  }
}

/** 券适用场景：AI 跟单（voucherapi.md §1.4.4） */
export const COUPON_SCENE_AI_COPYTRADE = "AI_COPYTRADE";

/** 券适用场景：CFD（voucherapi.md §1.4.4） */
export const COUPON_SCENE_CFD = "CFD";

/** 券适用场景：钱包兑现（真金券 CASH_CREDIT） */
export const COUPON_SCENE_WALLET = "WALLET";

/** 权益类型：真金入账 */
export const COUPON_BENEFIT_CASH_CREDIT = "CASH_CREDIT";

/** 权益类型：开仓抵扣（固定面额） */
export const COUPON_BENEFIT_FIXED_AMOUNT = "FIXED_AMOUNT";

/** 权益类型：亏损补贴（按比例） */
export const COUPON_BENEFIT_PERCENT_OFF = "PERCENT_OFF";

/** 用户券状态：可用（voucherapi.md §1.4.6） */
export const COUPON_USER_STATUS_AVAILABLE = "AVAILABLE";

/** 适用场景含 AI 跟单（用券下单） */
export function userCouponHasAiCopytradeScene(raw) {
  return parseUserCouponApplicableScenes(raw).includes(COUPON_SCENE_AI_COPYTRADE);
}

export function getCouponBenefitType(item) {
  const raw = item?.raw && typeof item.raw === "object" ? item.raw : item;
  return String(raw?.benefitType || raw?.benefit_type || item?.benefitType || "").toUpperCase();
}

/** 是否为真金券（CASH_CREDIT） */
export function isCashCreditCoupon(raw) {
  return getCouponBenefitType(raw) === COUPON_BENEFIT_CASH_CREDIT;
}

/** 是否为开仓抵扣券（FIXED_AMOUNT） */
export function isFixedAmountCoupon(item) {
  return getCouponBenefitType(item) === COUPON_BENEFIT_FIXED_AMOUNT;
}

/** 是否为亏损补贴券（PERCENT_OFF） */
export function isPercentOffCoupon(item) {
  return getCouponBenefitType(item) === COUPON_BENEFIT_PERCENT_OFF;
}

/** 是否展示「领取到账户」：AVAILABLE + CASH_CREDIT + WALLET 场景 + 未过期 */
export function canShowVoucherCashRedeem(item) {
  if (!item) return false;
  const raw = item.raw && typeof item.raw === "object" ? item.raw : item;
  if (!isCashCreditCoupon(raw)) return false;
  if (String(item.status || raw.status || "").toUpperCase() !== COUPON_USER_STATUS_AVAILABLE) return false;
  const scenes = parseUserCouponApplicableScenes(raw);
  if (scenes.length > 0 && !scenes.includes(COUPON_SCENE_WALLET)) return false;
  const expireAt = raw.expireAt || raw.expire_at;
  if (expireAt) {
    const expMs = new Date(expireAt).getTime();
    if (Number.isFinite(expMs) && expMs < Date.now()) return false;
  }
  return Boolean(item.id || raw.id);
}

function formatNumber(n) {
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 8 }).format(n);
}

/** 到期时间展示：YYYY-MM-DD H:MM AM/PM(UTC)，如 2024-12-24 0:00 AM(UTC) */
function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);

    const year = d.getUTCFullYear();
    const month = String(d.getUTCMonth() + 1).padStart(2, "0");
    const day = String(d.getUTCDate()).padStart(2, "0");
    const h24 = d.getUTCHours();
    const minutes = String(d.getUTCMinutes()).padStart(2, "0");
    const ampm = h24 >= 12 ? "PM" : "AM";
    let h12 = h24 % 12;
    if (h24 === 12) h12 = 12;

    return `${year}-${month}-${day} ${h12}:${minutes} ${ampm}(UTC)`;
  } catch {
    return String(iso);
  }
}

/**
 * @param {object} raw — UserCouponEntity（camelCase 或 snake_case）
 */
/** 兑换领券成功 result：{ userCoupon, template } 或单条 UserCouponEntity */
export function normalizeRedeemCouponResult(result) {
  if (!result || typeof result !== "object") return null;
  const entity = result.userCoupon ?? result.user_coupon ?? result;
  return normalizeUserCoupon(entity);
}

export function normalizeUserCoupon(raw) {
  if (!raw || typeof raw !== "object") return null;

  const id =
    raw.id != null && raw.id !== ""
      ? String(raw.id)
      : raw.userCouponId != null && raw.userCouponId !== ""
        ? String(raw.userCouponId)
        : raw.user_coupon_id != null && raw.user_coupon_id !== ""
          ? String(raw.user_coupon_id)
          : "";
  const benefitType = String(raw.benefitType || raw.benefit_type || "").toUpperCase();
  const benefitValue = Number(raw.benefitValue ?? raw.benefit_value);

  let amount = "—";
  let unit = "";
  if (benefitType === "PERCENT_OFF" && Number.isFinite(benefitValue)) {
    amount = String(benefitValue);
    unit = "%";
  } else if (Number.isFinite(benefitValue)) {
    amount = formatNumber(benefitValue);
    unit = "USDT";
  }

  const name = raw.displayName || raw.display_name || "";
  const displayDescription = String(raw.displayDescription ?? raw.display_description ?? "").trim();
  const codeLabel = id ? `#${id.length > 8 ? id.slice(-8) : id}` : "—";
  const expireAt = raw.expireAt || raw.expire_at;
  const expireTime = formatDateTime(expireAt);
  const status = String(raw.status || "AVAILABLE").toUpperCase();
  const applicableScenesList = parseUserCouponApplicableScenes(raw);

  return {
    id,
    amount,
    unit,
    benefitType,
    name,
    couponName: name,
    displayDescription,
    code: codeLabel,
    couponCode: codeLabel,
    expireTime,
    expiryDate: expireTime,
    status,
    applicableScenesList,
    raw,
  };
}

/** 主列表「可用」Tab：AVAILABLE + LOCKED */
export function isUsableCouponStatus(status) {
  const s = String(status || "").toUpperCase();
  return s === "AVAILABLE" || s === "LOCKED";
}

/** 历史页：已结束态（仅终态；全量记录见 isVoucherHistoryListStatus） */
export function isHistoryCouponStatus(status) {
  const s = String(status || "").toUpperCase();
  return s === "CONSUMED" || s === "EXPIRED" || s === "VOID";
}

/** 历史页列表：含可用/占用，与接口返回全量券一致 */
export function isVoucherHistoryListStatus(status) {
  const s = String(status || "").toUpperCase();
  return s === "AVAILABLE" || s === "LOCKED" || s === "CONSUMED" || s === "EXPIRED" || s === "VOID";
}

function parseIsoMs(iso) {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : 0;
}

/**
 * 与日期选择器关联：判断「该条记录」是否落在区间内。
 * 可用/占用看发放（或生效）；已核销/作废看各自时间；已过期看过期时刻。
 */
export function historyRangeAnchorMs(raw) {
  if (!raw || typeof raw !== "object") return 0;
  const st = String(raw.status || "").toUpperCase();
  if (st === "CONSUMED") return parseIsoMs(raw.consumedAt || raw.consumed_at);
  if (st === "VOID") return parseIsoMs(raw.voidAt || raw.void_at);
  if (st === "EXPIRED") return parseIsoMs(raw.expireAt || raw.expire_at);
  if (st === "LOCKED") {
    return parseIsoMs(
      raw.lockedAt || raw.locked_at || raw.grantedAt || raw.granted_at || raw.effectiveAt || raw.effective_at
    );
  }
  if (st === "AVAILABLE") {
    return parseIsoMs(raw.grantedAt || raw.granted_at || raw.effectiveAt || raw.effective_at || raw.createdAt || raw.created_at);
  }
  return parseIsoMs(raw.updatedAt || raw.updated_at || raw.createdAt || raw.created_at);
}

/** 列表排序：新事件在上 */
export function historySortTimeMs(raw) {
  if (!raw || typeof raw !== "object") return 0;
  const st = String(raw.status || "").toUpperCase();
  if (st === "CONSUMED") return parseIsoMs(raw.consumedAt || raw.consumed_at);
  if (st === "VOID") return parseIsoMs(raw.voidAt || raw.void_at);
  if (st === "EXPIRED") return parseIsoMs(raw.expireAt || raw.expire_at || raw.updatedAt || raw.updated_at);
  if (st === "LOCKED") {
    return parseIsoMs(raw.lockedAt || raw.locked_at || raw.grantedAt || raw.granted_at);
  }
  if (st === "AVAILABLE") {
    return parseIsoMs(raw.grantedAt || raw.granted_at || raw.effectiveAt || raw.effective_at || raw.createdAt || raw.created_at);
  }
  return parseIsoMs(raw.updatedAt || raw.updated_at || raw.createdAt || raw.created_at);
}

/** 券历史卡片备注：coupons 列表的 templateRemark / templateRemarkI18n */
export function resolveVoucherHistoryRemark(raw, locale) {
  if (!raw || typeof raw !== "object") return "";
  const fromI18n = pickCouponI18nText(raw.templateRemarkI18n ?? raw.template_remark_i18n, locale);
  if (fromI18n) return fromI18n;
  const plain = raw.templateRemark ?? raw.template_remark;
  if (plain != null && String(plain).trim()) return String(plain).trim();
  return "";
}

/** 历史行右侧展示用时间点 */
export function historyDisplayTimeIso(raw) {
  if (!raw || typeof raw !== "object") return "";
  const st = String(raw.status || "").toUpperCase();
  if (st === "CONSUMED") return raw.consumedAt || raw.consumed_at || "";
  if (st === "VOID") return raw.voidAt || raw.void_at || "";
  if (st === "EXPIRED") return raw.expireAt || raw.expire_at || "";
  if (st === "LOCKED") return raw.lockedAt || raw.locked_at || raw.grantedAt || raw.granted_at || "";
  return raw.grantedAt || raw.granted_at || raw.effectiveAt || raw.effective_at || raw.createdAt || raw.created_at || "";
}
